package it.neckar.lizergy.model.financial.quote

import it.neckar.commons.tags.Tags
import it.neckar.lizergy.model.financial.currency.Money
import it.neckar.lizergy.model.financial.currency.ValueAddedTax
import it.neckar.lizergy.model.financial.currency.sum
import it.neckar.lizergy.model.financial.quote.query.Query
import it.neckar.open.collections.fastMap
import it.neckar.open.time.sum
import it.neckar.open.unit.currency.EUR
import it.neckar.open.unit.other.pct
import it.neckar.uuid.NativeUuid
import it.neckar.uuid.randomForEnvironment
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Duration
import kotlin.uuid.Uuid

/**
 * A section within a quote that contains further [QuoteElement]s
 */
@Serializable
@SerialName("QuoteSection")
data class QuoteSection(
  override val uuid: NativeUuid,

  /**
   * The headline
   */
  override val headline: String,

  override val amount: Amount? = null,

  override val factor: @pct Double? = null,

  override val visibility: Visibility,

  override val details: String? = null,

  /**
   * The children.
   * All children must share the same visibility!
   */
  val children: List<QuoteElement>,

  ) : QuoteElement {

  constructor(
    headline: String,
    amount: Amount? = null,
    factor: @pct Double? = null,
    visibility: Visibility,
    details: String? = null,
    children: List<QuoteElement>,
  ) : this(
    uuid = Uuid.randomForEnvironment(),
    headline = headline,
    amount = amount,
    factor = factor,
    visibility = visibility,
    details = details,
    children = children,
  )

  init {
    require(headline.isNotBlank()) {
      "Headline must not be blank"
    }

    require(
      children.asSequence()
        .distinctBy { it.visibility }
        .count() <= 1
    ) {
      "Only children with the same visibility are supported! But was:\n${children.joinToString("\n") { "- ${it.visibility.name}: ${it.headline}" }}"
    }
  }

  override val optionality: Optionality
    get() = if (children.all { it.optionality == Optionality.Optional }) Optionality.Optional else Optionality.Mandatory

  override val netPrice: @EUR Money
    get() = children.fastMap { it.netPrice }.sum()

  override fun queryQuoteElement(query: Query): QuoteSection? {
    val queriedChildren = children.mapNotNull { it.queryQuoteElement(query) }
    if (queriedChildren.isEmpty()) return null
    return copy(children = queriedChildren)
  }

  override fun listQuoteItems(query: Query): List<QuoteItem> {
    return children.flatMap { it.listQuoteItems(query) }
  }

  override fun vats(query: Query): List<ValueAddedTax> {
    return children.flatMap { it.vats(query) }.distinct().sortedBy { it.vat }
  }

  override fun workingTimes(query: Query): Duration {
    return children.fastMap { it.workingTimes(query) }.sum()
  }

  override fun relevanceOrNull(): Tags? {
    return null
  }

}
